import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@app/ui';
import { PageTransition, StaggerContainer, StaggerItem } from '@app/ui/motion';
import {
  type CompensationItemInput,
  type CreateDealInput,
  createDealSchema,
} from '@app/validators';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CompensationEditor } from '../../../components/compensation-editor';
import { type PayorOption, PayorTypeahead } from '../../../components/payor-typeahead';
import { RouteErrorComponent } from '../../../components/route-error';
import { api, client } from '../../../lib/api';
import { handleFeatureGateError } from '../../../lib/feature-gate-client';
import { compressImageToDataUrl, UnsupportedImageFormatError } from '../../../lib/image';

export const Route = createFileRoute('/_authenticated/deals/new')({
  // Accept ?payorId=<id> so the "add new payor" flow can hand us back
  // the newly-created payor to pre-select in the typeahead. Return type
  // is annotated as fully-optional so existing `<Link to="/deals/new">`
  // sites elsewhere keep typechecking without an explicit `search={}`.
  validateSearch: (search: Record<string, unknown>): { payorId?: string } => ({
    payorId: typeof search.payorId === 'string' ? search.payorId : undefined,
  }),
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: NewDeal,
});

function NewDeal() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { payorId: incomingPayorId } = Route.useSearch();

  const form = useForm<CreateDealInput>({
    resolver: zodResolver(createDealSchema),
    defaultValues: {
      title: '',
      brandName: '',
      amount: 0,
      brandContact: '',
      startDate: '',
      endDate: '',
      source: '',
      dealSource: 'self_sourced',
      description: '',
      isExclusive: false,
    },
  });

  const [dmText, setDmText] = useState('');
  const [extracting, setExtracting] = useState(false);
  // US-028: typeahead-selected payor. When set, we send `payorId` on
  // submit and the server auto-syncs brandName from the canonical
  // legalName. The brandName field stays in the form (read-only) so
  // existing form validation and react-hook-form state still work.
  const [selectedPayor, setSelectedPayor] = useState<PayorOption | undefined>(undefined);

  // Hand-off from the "+ Add new payor" flow on the typeahead: when we
  // return to /deals/new with ?payorId=<id>, fetch the new payor and
  // pre-select it so the athlete picks up where they left off. Mirrors
  // the brand-sync the typeahead does on a manual selection (legalName +
  // contact onto the form fields).
  const incomingPayorQuery = useQuery({
    ...api.payor.get.queryOptions({ input: { id: incomingPayorId ?? '' } }),
    enabled: !!incomingPayorId,
  });
  useEffect(() => {
    const p = incomingPayorQuery.data;
    if (!p || selectedPayor?.id === p.id) return;
    setSelectedPayor({
      id: p.id,
      legalName: p.legalName,
      displayName: p.displayName ?? null,
      contactName: p.contactName ?? null,
      contactEmail: p.contactEmail ?? null,
      contactPhone: p.contactPhone ?? null,
      validationStatus: p.validationStatus as PayorOption['validationStatus'],
    });
    form.setValue('brandName', p.legalName);
    if (p.contactName) form.setValue('brandContact', p.contactName);
  }, [incomingPayorQuery.data, selectedPayor?.id, form]);

  // US-029: structured compensation items. Defaults to one CASH row so
  // the zero-extra-clicks path still works. The legacy `amount` form
  // field is auto-synced to the sum on every change.
  const [compensationItems, setCompensationItems] = useState<CompensationItemInput[]>([
    { type: 'CASH', amount: 0, currency: 'USD', description: 'Cash payment', orderIndex: 0 },
  ]);

  const createMutation = useMutation({
    ...api.deal.create.mutationOptions(),
    onSuccess: (result) => {
      // Invalidate every deal query — `list` (filtered by status, paginated),
      // `dashboard`, and `activeCount` (drives the plan-cap gate on the
      // deals list). Stale on any one of these would mislead the UI; the
      // predicate keeps the call site honest as we add more deal queries.
      queryClient.invalidateQueries({
        // oRPC queryKey is [path[], options]; queryKey[0] is the path array.
        predicate: (query) => Array.isArray(query.queryKey?.[0]) && query.queryKey[0][0] === 'deal',
      });
      navigate({ to: '/deals/$dealId', params: { dealId: result.id } });
    },
    onError: (e) => {
      // The MutationCache interceptor (router.tsx) redirects to /pricing
      // when a FEATURE_GATE response fires; don't flash an error toast
      // during that redirect.
      if (handleFeatureGateError(e)) return;
      toast.error(`Failed to create deal: ${e.message}`);
    },
  });

  const applyExtraction = (result: {
    suggestedTitle?: string;
    brandName?: string;
    brandContact?: string;
    compensation?: Array<{
      type: 'CASH' | 'GOODS_SERVICES' | 'BONUS' | 'ROYALTY';
      amount: number;
      description: string;
      royaltyPercent?: number;
      expectedVolumeLow?: number;
      expectedVolumeHigh?: number;
      unitLabel?: string;
    }>;
    startDate?: string;
    endDate?: string;
    isExclusive?: boolean;
    notes?: string;
    deliverables?: Array<{ title: string; type?: string; platform?: string; dueDate?: string }>;
  }) => {
    // shouldDirty + shouldTouch make react-hook-form's Controller-wrapped
    // FormField inputs actually re-render with the new values. Without
    // these the form state updates silently and the user sees stale
    // empty inputs — the original "AI only fills the description" bug.
    const setOpts = { shouldDirty: true, shouldTouch: true, shouldValidate: true };

    // Title: prefer the AI's punchy suggestion; fall back to "<brand> Deal".
    // suggestedTitle is required-non-empty per the new schema, but guard
    // anyway in case an older payload sneaks through.
    if (result.suggestedTitle) {
      form.setValue('title', result.suggestedTitle, setOpts);
    } else if (result.brandName) {
      form.setValue('title', `${result.brandName} Deal`, setOpts);
    }
    // brandName always populates now — the AI returns a bracketed
    // placeholder like "[Sender's store — confirm name]" when the
    // texter is implicitly the brand. Populating it lets the athlete
    // edit in-place instead of staring at a blank field.
    if (result.brandName) form.setValue('brandName', result.brandName, setOpts);
    if (result.brandContact) form.setValue('brandContact', result.brandContact, setOpts);
    if (result.startDate) form.setValue('startDate', result.startDate, setOpts);
    if (result.endDate) form.setValue('endDate', result.endDate, setOpts);
    if (typeof result.isExclusive === 'boolean') {
      form.setValue('isExclusive', result.isExclusive, setOpts);
    }

    // Compensation: replace the editor's items with the AI's structured
    // list. Each item maps 1:1 to a row in the CompensationEditor.
    //
    // Tested against the user's "$500/month + swag bag" + "10% jersey
    // proceeds" thread, the AI returns three items (CASH 500,
    // GOODS_SERVICES "Swag bag", ROYALTY 10% × 1000 jerseys) — all
    // three need to land on the visible UI, not get summarized away.
    if (result.compensation && result.compensation.length > 0) {
      const items: CompensationItemInput[] = result.compensation.map((c, i) => ({
        type: c.type,
        amount: c.amount,
        currency: 'USD',
        description: c.description,
        royaltyPercent: c.royaltyPercent,
        expectedVolumeLow: c.expectedVolumeLow,
        expectedVolumeHigh: c.expectedVolumeHigh,
        unitLabel: c.unitLabel,
        orderIndex: i,
      }));
      setCompensationItems(items);
      // The hidden legacy form.amount stays in sync with the sum so
      // the existing zod validation on `amount` doesn't reject the
      // submit. Auto-summed on submit too, but keep this consistent
      // in case validation runs before submit.
      const sum = items.reduce((s, it) => s + Number(it.amount || 0), 0);
      form.setValue('amount', sum, setOpts);
    }

    // Deliverables can't be created at /deals/new (they live on the
    // deal once it exists). Surface them in the description so the
    // user sees what the AI extracted and can add them on the deal
    // page after save instead of silently dropping the data.
    const deliverableNote =
      result.deliverables && result.deliverables.length > 0
        ? `Deliverables (add on the deal page after saving):\n${result.deliverables
            .map((d) => {
              const parts = [
                d.title,
                d.platform ? `(${d.platform})` : null,
                d.dueDate ? `due ${d.dueDate}` : null,
              ].filter(Boolean);
              return `- ${parts.join(' ')}`;
            })
            .join('\n')}`
        : '';
    const description = [result.notes, deliverableNote].filter(Boolean).join('\n\n');
    if (description) form.setValue('description', description, setOpts);
  };

  const handleExtractText = async () => {
    if (!dmText.trim()) return;
    setExtracting(true);
    try {
      const result = await client.ai.extractFromText({ text: dmText });
      applyExtraction(result);
    } catch (e) {
      // Plan-gate hits redirect to /pricing; only toast non-gate errors.
      if (handleFeatureGateError(e)) return;
      // Server-side AI failures (quota reached, key unset) already arrive
      // with an operator-readable message from withAiErrorHandling — show
      // those verbatim rather than double-prefixing them.
      const msg = e instanceof Error ? e.message : 'Unknown error';
      toast.error(msg.startsWith('AI service unavailable') ? msg : `AI extraction failed: ${msg}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      // Downscale + JPEG re-encode before sending. A 4K phone
      // screenshot base64-encoded is ~5-7 MB on the wire, which trips
      // Vercel's function body-size limit; 1280px JPEG is ~150-400 KB.
      const dataUrl = await compressImageToDataUrl(file);
      const result = await client.ai.extractFromScreenshot({ imageUrl: dataUrl });
      applyExtraction(result);
    } catch (e) {
      if (handleFeatureGateError(e)) return;
      // Format-specific errors (HEIC etc.) get the friendly remediation
      // message verbatim — no "AI extraction failed:" prefix because the
      // failure is in the upload, not the model.
      if (e instanceof UnsupportedImageFormatError) {
        toast.error(e.message);
        return;
      }
      // Server-side AI failures (quota reached, key unset) already arrive
      // with an operator-readable message from withAiErrorHandling — show
      // those verbatim rather than double-prefixing them.
      const msg = e instanceof Error ? e.message : 'Unknown error';
      toast.error(msg.startsWith('AI service unavailable') ? msg : `AI extraction failed: ${msg}`);
    } finally {
      setExtracting(false);
    }
  };

  const onSubmit = (data: CreateDealInput, status: 'DRAFT' | 'ACTIVE') => {
    // US-029: validate items before mutate so the user sees a friendly
    // toast instead of a server-side error.
    if (compensationItems.length === 0) {
      toast.error('At least one compensation item is required');
      return;
    }
    if (compensationItems.some((it) => !it.description?.trim())) {
      toast.error('Each compensation item needs a short description');
      return;
    }
    if (compensationItems.every((it) => !Number(it.amount))) {
      toast.error("Add at least one compensation amount that's greater than 0");
      return;
    }
    const computedTotal = compensationItems.reduce((s, it) => s + Number(it.amount || 0), 0);

    createMutation.mutate({
      title: data.title,
      // When a payor is selected we still send brandName (it's required by
      // the validator) but the server will overwrite it with the payor's
      // canonical legalName. This keeps the legacy column in sync.
      brandName: selectedPayor?.legalName ?? data.brandName,
      payorId: selectedPayor?.id,
      // US-029: the cached `amount` is the sum of items. We send it so
      // existing readers that haven't migrated to compensationItems
      // still see the right total; the server recomputes regardless.
      amount: computedTotal,
      status,
      brandContact: selectedPayor?.contactName ?? data.brandContact ?? undefined,
      description: data.description || undefined,
      startDate: data.startDate || undefined,
      endDate: data.endDate || undefined,
      source: data.source || undefined,
      dealSource: data.dealSource,
      isExclusive: data.isExclusive,
      compensationItems,
    });
  };

  return (
    <PageTransition>
      <div className="mb-6">
        <Link to="/deals" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; Back to Deals
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold mt-2">New Deal</h1>
      </div>

      <StaggerContainer className="grid lg:grid-cols-3 gap-6">
        {/* AI Import */}
        <StaggerItem className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Quick Import</CardTitle>
              <CardDescription>Upload a screenshot or paste text to auto-fill</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* biome-ignore lint/a11y/useSemanticElements: drop zone with nested interactive content */}
              <div
                role="button"
                tabIndex={0}
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
                }}
              >
                <p className="text-sm text-muted-foreground mb-2">Drop a DM screenshot here</p>
                <Button variant="outline" size="sm" type="button">
                  Choose File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleScreenshotUpload}
                />
              </div>
              <div className="text-center text-sm text-muted-foreground">or</div>
              <Textarea
                placeholder="Paste DM conversation text here..."
                className="min-h-[100px]"
                value={dmText}
                onChange={(e) => setDmText(e.target.value)}
              />
              <Button
                className="w-full"
                variant="secondary"
                onClick={handleExtractText}
                disabled={extracting || !dmText.trim()}
              >
                {extracting ? 'Extracting...' : 'Extract Deal Info'}
              </Button>
            </CardContent>
          </Card>
        </StaggerItem>

        {/* Manual Form */}
        <StaggerItem className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Deal Details</CardTitle>
              <CardDescription>
                Enter deal information manually or review AI-extracted data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  className="space-y-4"
                  onSubmit={form.handleSubmit((data) => onSubmit(data, 'ACTIVE'))}
                >
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Deal Title</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g., Nike Instagram Campaign" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormItem>
                      <FormLabel>Who's paying you?</FormLabel>
                      <FormControl>
                        <PayorTypeahead
                          value={selectedPayor}
                          onSelect={(p) => {
                            setSelectedPayor(p);
                            // Mirror onto the legacy brandName field so
                            // existing form validators are satisfied.
                            if (p) {
                              form.setValue('brandName', p.legalName);
                              if (p.contactName) form.setValue('brandContact', p.contactName);
                            }
                          }}
                          onRequestCreate={(initial) =>
                            navigate({
                              to: '/payors/$payorId',
                              params: { payorId: 'new' },
                              // returnTo: bounce back to /deals/new after save
                              // so the athlete keeps going instead of
                              // landing stranded on the payor page.
                              search: { name: initial, returnTo: '/deals/new' },
                            })
                          }
                          placeholder="Type a brand or business name"
                        />
                      </FormControl>
                      {!selectedPayor && (
                        <p className="text-xs text-muted-foreground">
                          Pick an existing payor or start typing to add a new one.
                        </p>
                      )}
                    </FormItem>
                  </div>

                  {/* Legacy brandName remains in the form for backward
                      compatibility. When a payor is selected it's a
                      read-only mirror of the canonical legalName; without
                      one, the athlete can still free-type. */}
                  <FormField
                    control={form.control}
                    name="brandName"
                    render={({ field }) => (
                      <FormItem className={selectedPayor ? 'hidden' : ''}>
                        <FormLabel>Brand Name (legacy)</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Nike" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* US-029: structured compensation editor (replaces the
                      legacy single Deal Amount input). The legacy `amount`
                      field stays in the form to satisfy zod validation but
                      its value is auto-set from the sum on submit. */}
                  <CompensationEditor
                    items={compensationItems}
                    onChange={setCompensationItems}
                    currency="USD"
                  />

                  <FormField
                    control={form.control}
                    name="brandContact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Brand Contact</FormLabel>
                        <FormControl>
                          <Input placeholder="Contact person name" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="endDate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>End Date</FormLabel>
                          <FormControl>
                            <Input type="date" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Outreach channel</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="How did the brand reach you?" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="dm">Instagram DM</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="agent">Agent</SelectItem>
                            <SelectItem value="direct">Direct Contact</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dealSource"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>How did you find this deal?</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select origin..." />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="self_sourced">
                              I found this myself (no NilBoard marketplace fee)
                            </SelectItem>
                            <SelectItem value="nilboard_marketplace">
                              I discovered the brand via NilBoard
                            </SelectItem>
                            <SelectItem value="brand_outbound">
                              The brand discovered me via NilBoard
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          This determines the marketplace fee (if any). See{' '}
                          <Link to="/pricing" className="underline">
                            pricing
                          </Link>
                          .
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description / Notes</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Deal details, deliverables, terms..."
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="isExclusive"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="font-normal">This is an exclusive deal</FormLabel>
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
                    <Link to="/deals">
                      <Button
                        variant="outline"
                        className="w-full sm:w-auto min-h-[44px]"
                        type="button"
                      >
                        Cancel
                      </Button>
                    </Link>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-[44px]"
                      disabled={createMutation.isPending}
                      onClick={form.handleSubmit((data) => onSubmit(data, 'DRAFT'))}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      type="submit"
                      className="min-h-[44px]"
                      disabled={createMutation.isPending}
                    >
                      {createMutation.isPending ? 'Creating...' : 'Create Deal'}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </StaggerItem>
      </StaggerContainer>
    </PageTransition>
  );
}
